import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { navigateTop } from 'common/utils';
import { useMediaQuery } from 'react-responsive';
import styles from './Postulation.module.scss';
import { SessionState } from 'features/session/sessionSlice';
import {
  useGetPostulationQuery,
  useUpdatePostulationMutation,
  useAddPostulationFilesMutation,
  useUpdateBackofficePostulationMutation,
  useGetBackofficePostulationQuery,
  useAddBackofficePostulationFilesMutation,
} from 'services/exploreApi';
import Text from 'common/components/Text/Text';
import { documentText } from 'common/constants';
import {
  ProgramExam,
  StudyOption,
  IntakeDate,
  PostulationRequirement,
  Postulation,
  ApiObject,
} from 'app/types';
import UploadCard from './UploadCard';
import { Star } from 'assets/user/index';
import Robot from 'assets/profile/robot.svg';
import { ServicesPhoto } from 'assets/session/dashboard/index';
import { useFormik } from 'formik';
import Loader from 'common/components/Loader/Loader';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  setApplicationSubmitted,
  setCvUploaded,
  setLastStepDone,
  setTestsScoresUploaded,
  UserState,
} from '../userSlice';
import ExpertCard from '../cards/ExpertCard';
import { detailUrl } from 'common/utils/conversions';
import CollapseCard from 'common/components/CollapseCard/CollapseCard';

interface CompProps {
  studyOption: StudyOption;
  intakeDate: IntakeDate;
  handleClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  active: boolean;
}

const statusList: { [key: string]: number } = {
  undefined: 1,
  pending: 1,
  preparing: 2,
  submitted: 2,
  need_revision: 2,
  submitted_approved: 3,
  additional_files: 3,
  school_accepted: 4,
};

interface UploadFiles {
  cv: File | null;
  englishProof: File[] | null;
  technicalTest: File[] | null;
  referenceLetter: File[] | null;
  transcripts: File | null;
  personalStatement: File | null;
  scholarshipLetter: File | null;
  degreeCertificate: File | null;
  universityFee: File | null;
  additionalDocuments: File[] | null;
}

const StudyOptionCard = ({
  studyOption,
  intakeDate,
  handleClick,
  active,
}: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <>
      {isMobile ? (
        <button
          className={`${styles.studyOptionCard} ${active ? styles.active : ''} btn`}
          onClick={handleClick}
        >
          <div className={styles.header}>
            <i className={`bi bi-check-circle ${active ? styles.active : ''}`}></i>
            <Text size={12} weight={500}>
              Option
            </Text>
          </div>
          <div className={styles.firstRow}>
            <span>{`Mode: ${studyOption.schedule}`}</span>
            <span>{`Intake month: ${intakeDate.intake_month}`}</span>
          </div>
          <div className={styles.secondRow}>
            <span>{`Duration: ${studyOption.duration_in_months} months`}</span>
            <span>{`Fee: ${studyOption.tuition_fee?.amount_international}${studyOption.tuition_fee?.currency} per year`}</span>
          </div>
        </button>
      ) : (
        <div className={`${styles.studyOptionCard} ${active ? styles.active : ''}`}>
          <div className="row mx-0 d-flex align-items-center h-100">
            <div className="col-1 text-center">
              <i className={`bi bi-check-circle col-1 ${active ? styles.active : ''}`}></i>
            </div>
            <div className="col-2">
              <Text size={16} weight={400} color="primary">
                {studyOption.schedule === 'full_time' ? 'Full Time' : 'Part Time'}
              </Text>
            </div>
            <div className="col-2">
              <Text size={16} weight={400} color="primary">
                {intakeDate.intake_month}
              </Text>
            </div>
            <div className="col-2">
              <Text size={16} weight={400} color="primary">
                {studyOption.duration_in_months} months
              </Text>
            </div>
            <div className="col-3">
              <Text size={16} weight={400} color="primary">
                {`${studyOption.tuition_fee?.amount_international}${studyOption.tuition_fee?.currency} per year`}
              </Text>
            </div>
            <div className="col-2">
              <button onClick={handleClick} className={`${styles.button} btn`}>
                Choose
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const handleFormName = (step: number) => {
  if (step === 2) {
    return 'filesForm';
  } else if (step === 4) {
    return 'universityFeeForm';
  } else {
    return 'form';
  }
};
type UploadFilesKey = keyof UploadFiles;

const PostulationSetUp: FunctionComponent = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const location = useLocation();
  let submitPostulation: any;
  let isLoadingPostulation: boolean;
  const isBackoffice = location.pathname.includes('backoffice');
  const { id, postulation_id } = useParams();
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [currentChoice, setCurrentChoice] = useState<StudyOption>();
  const [currentIntakeDate, setCurrentIntakeDate] = useState<IntakeDate>();
  const [readyForSubmit, setReadyForSubmit] = useState<boolean>(false);
  const [submitFormButton, setSubmitFormButton] = useState<boolean>(false);
  const [postulationSubmitted, setPostulationSubmitted] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<StudyOption[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<{ [k: string]: any }>({});
  let postulation: ApiObject<Postulation> | undefined;
  let isLoading: boolean;
  let isLoadingFiles: boolean;
  let submitPostulationFiles: any;
  let refetch: any;
  if (isBackoffice) {
    ({
      data: postulation,
      isLoading,
      refetch,
    } = useGetBackofficePostulationQuery(postulation_id || ''));
    [submitPostulation, { isLoading: isLoadingPostulation }] =
      useUpdateBackofficePostulationMutation();
    [submitPostulationFiles, { isLoading: isLoadingFiles }] =
      useAddBackofficePostulationFilesMutation();
  } else {
    ({ data: postulation, isLoading, refetch } = useGetPostulationQuery(parseInt(id || '')));
    [submitPostulation, { isLoading: isLoadingPostulation }] = useUpdatePostulationMutation();
    [submitPostulationFiles, { isLoading: isLoadingFiles }] = useAddPostulationFilesMutation();
  }
  const dispatch = useAppDispatch();
  const locationFilter = [
    ...new Set(
      postulation?.data.program.study_options.map((studyOption: StudyOption) => {
        return studyOption.city.name;
      })
    ),
  ];
  useEffect(() => {
    if (postulation) {
      setFilterData([...postulation.data.program.study_options]);
    }
  }, [postulation]);
  const preFilter = postulation?.data.program.study_options.map((studyOption: StudyOption) => {
    const years = studyOption.intake_dates?.map((intakeDate: IntakeDate) => {
      return new Date(intakeDate.date).getFullYear();
    });
    return years;
  });
  const attendanceFilter = [
    ...new Set(
      postulation?.data.program.study_options.map((studyOption: StudyOption) => {
        return studyOption.delivery;
      })
    ),
  ];

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>, filter: string) => {
    if (event.target.value === 'all') {
      setAppliedFilters(({ [filter]: _, ...rest }) => rest);
    } else {
      setAppliedFilters((sf) => ({ ...sf, [filter]: event.target.value }));
    }
  };

  useEffect(() => {
    if (Object.keys(appliedFilters).length === 0 && postulation) {
      setFilterData(postulation?.data.program.study_options);
    } else if (postulation) {
      let arr = [...postulation.data.program.study_options];
      if (appliedFilters['intake_date'] !== undefined) {
        arr = arr.filter((studyOption: StudyOption) => {
          const int = studyOption.intake_dates?.filter((intakeDate: IntakeDate) => {
            return new Date(intakeDate.date).getFullYear() == appliedFilters['intake_date'];
          });
          return int ? int.length > 0 : false;
        });
      }
      if (appliedFilters['attendance'] !== undefined) {
        arr = arr.filter((studyOption: StudyOption) => {
          return studyOption.delivery === appliedFilters['attendance'];
        });
      }
      if (appliedFilters['location'] !== undefined) {
        arr = arr.filter((studyOption: StudyOption) => {
          return studyOption.city.name === appliedFilters['location'];
        });
      }
      setFilterData(arr);
    }
  }, [appliedFilters]);
  const intakeDatesFilter = [...new Set(preFilter?.flat())];
  const navigate = useNavigate();
  const { profileCompleted } = useAppSelector(({ user }: { user: UserState }) => user);
  const intakeDatesOptions = intakeDatesFilter.map((year) => (
    <option key={year} value={year}>
      {year}
    </option>
  ));
  const attendaceFilterOptions = attendanceFilter.map((attendance) => (
    <option key={attendance} value={attendance}>
      {attendance}
    </option>
  ));
  const locationFilterOptions = locationFilter.map((location) => (
    <option key={location} value={location}>
      {location}
    </option>
  ));
  const [currentStep, setCurrentStep] = useState<number>(
    statusList[postulation?.data.status || 'pending']
  );
  const handleClick = (event: any) => {
    if (postulation?.data.status === 'pending') {
      setCurrentChoice(event.studyOption);
      setCurrentIntakeDate(event.intakeDate);
    } else {
      console.log('Implement notification');
    }
  };
  useEffect(() => {
    if (postulation?.data.study_option) {
      const list = postulation?.data.program.study_options.filter(
        (studyOption: StudyOption) => studyOption.id === postulation?.data.study_option.id
      );
      setCurrentChoice(list[0]);
      setCurrentStep(statusList[postulation?.data.status || 'pending']);
    }
  }, [postulation]);

  useEffect(() => {
    refetch();
  }, []);

  const checkReadyForSubmit = () => {
    if (
      postulation?.data.program.program_exams.filter(
        (item: ProgramExam) => item.exam.name == 'GMAT'
      ).length
    ) {
      // check for field
      if (
        profileCompleted &&
        postulation?.data.status !== 'need_revision' &&
        (postulation?.data.transcripts != 'missing' || formik.values.transcripts) &&
        (postulation?.data.english_score != 'missing' || formik.values.englishProof) &&
        (postulation?.data.cv != 'missing' || formik.values.cv) &&
        (postulation?.data.personal_statement != 'missing' || formik.values.personalStatement) &&
        (postulation?.data.degree_certificate != 'missing' || formik.values.degreeCertificate) &&
        (postulation?.data.technical_score != 'missing' || formik.values.technicalTest)
      ) {
        setReadyForSubmit(true);
        return;
      }
    } else {
      // check only for mandatory
      if (
        profileCompleted &&
        postulation?.data.status !== 'need_revision' &&
        (postulation?.data.transcripts != 'missing' || formik.values.transcripts) &&
        (postulation?.data.english_score != 'missing' || formik.values.englishProof) &&
        (postulation?.data.cv != 'missing' || formik.values.cv) &&
        (postulation?.data.degree_certificate != 'missing' || formik.values.degreeCertificate) &&
        (postulation?.data.personal_statement != 'missing' || formik.values.personalStatement)
      ) {
        setReadyForSubmit(true);
        return;
      }
    }
    setReadyForSubmit(false);
  };

  const checkPostulationSubmitted = () => {
    if (postulation?.data.status === 'submitted' || postulation?.data.status === 'need_revision') {
      setPostulationSubmitted(true);
    }
  };

  const handleSubmitChoice = (fromButton: string) => {
    const formData = new FormData();
    formData.append('study_option_id', currentChoice?.id.toString() || '');
    formData.append('intake_date_id', currentIntakeDate?.id.toString() || '');
    if (postulation?.data.study_option === null) {
      currentChoice && currentIntakeDate && formData.append('status', 'preparing');

      const values = {
        id: parseInt(isBackoffice ? postulation_id || '' : id || ''),
        data: formData,
      };
      submitPostulation(values).then((resp: any) => {
        currentChoice && currentIntakeDate && setCurrentStep(2);
        fromButton === 'save' && navigate(isBackoffice ? `/backoffice/users/${user?.id}` : '/home');
      });
    }
  };

  const handleSubmitPostulation = () => {
    const formData = new FormData();
    formData.append('status', 'submitted');
    if (postulation?.data.status == 'preparing' || postulation?.data.status == 'pending') {
      const values = { id: parseInt(id || ''), data: formData };
      if (isBackoffice) {
        submitPostulation(values).then((resp: any) => {
          setPostulationSubmitted(true);
        });
      } else {
        submitPostulation(values).then((resp: any) => {
          setPostulationSubmitted(true);
          dispatch(setApplicationSubmitted(true));
          dispatch(setLastStepDone(9));
        });
      }
    }
  };

  const handleConfirmOffer = () => {
    const formData = new FormData();
    formData.append('status', 'accepted');
    const values = { id: parseInt(id || ''), data: formData };
    submitPostulation(values).then((resp: any) => {
      isBackoffice ? navigate('/backoffice/home') : navigate('/home');
    });
  };

  const formik = useFormik<UploadFiles>({
    initialValues: {
      cv: null,
      englishProof: null,
      technicalTest: null,
      referenceLetter: null,
      transcripts: null,
      personalStatement: null,
      scholarshipLetter: null,
      degreeCertificate: null,
      universityFee: null,
      additionalDocuments: null,
    },
    onSubmit: async (uploadFiles: UploadFiles) => {
      if (currentStep === 2) {
        const formData = new FormData();
        if (postulation) {
          formData.append('id', String(postulation.data.id));
          if (uploadFiles.cv) {
            formData.append('postulation[cv]', uploadFiles.cv);
          }
          if (uploadFiles.englishProof) {
            uploadFiles.englishProof.map((photo) =>
              formData.append(`postulation[english_score][]`, photo)
            );
          }
          if (uploadFiles.technicalTest) {
            uploadFiles.technicalTest.map((photo) =>
              formData.append(`postulation[technical_score][]`, photo)
            );
          }
          if (uploadFiles.referenceLetter) {
            uploadFiles.referenceLetter.map((photo) =>
              formData.append(`postulation[recommendation_letter][]`, photo)
            );
          }
          if (uploadFiles.transcripts) {
            formData.append('postulation[transcripts]', uploadFiles.transcripts);
          }
          if (uploadFiles.personalStatement) {
            formData.append('postulation[personal_statement]', uploadFiles.personalStatement);
          }
          if (uploadFiles.degreeCertificate) {
            formData.append('postulation[degree_certificate]', uploadFiles.degreeCertificate);
          }
          if (uploadFiles.additionalDocuments) {
            uploadFiles.additionalDocuments.map((photo) =>
              formData.append(`postulation[additional_documents][]`, photo)
            );
          }
          // uploadFiles.scholarshipLetter
          //   ? formData.append('postulation[scholarship_letter]', uploadFiles.scholarshipLetter)
          //   : '';
          if (isBackoffice) {
            await submitPostulationFiles({ data: formData, id: postulation?.data.id })
              .unwrap()
              .then((res: any) => {
                if (submitFormButton) {
                  handleSubmitPostulation();
                } else {
                  navigate(`/backoffice/users/${user?.id}`);
                }
              });
          } else {
            await submitPostulationFiles({ data: formData, id: postulation?.data.id })
              .unwrap()
              .then((res: any) => {
                if (uploadFiles.cv) {
                  dispatch(setCvUploaded(true));
                  dispatch(setLastStepDone(7));
                }
                if (uploadFiles.englishProof) {
                  dispatch(setTestsScoresUploaded(true));
                  dispatch(setLastStepDone(8));
                }
                if (submitFormButton) {
                  handleSubmitPostulation();
                } else {
                  navigate('home/application_list');
                }
              });
          }
        }
      } else if (currentStep === 4) {
        if (postulation) {
          const formData = new FormData();
          formData.append('id', String(postulation.data.id));
          uploadFiles.universityFee
            ? formData.append('postulation[university_fee]', uploadFiles.universityFee)
            : '';
          await submitPostulationFiles({ data: formData, id: postulation?.data.id })
            .unwrap()
            .then((res: any) => {
              if (submitFormButton) {
                handleSubmitPostulation();
              } else {
                navigate(isBackoffice ? 'backoffice/home' : `/backoffice/users/${user?.id}`);
              }
            });
        }
      }
    },
  });

  const handleDocumentStatus = (formName: keyof UploadFiles, status: string) => {
    if (status !== 'missing') {
      return status;
    } else {
      return formik.values[formName] ? 'uploaded' : 'missing';
    }
  };

  useEffect(() => {
    checkReadyForSubmit();
    checkPostulationSubmitted();
  }, [formik]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    formik.setFieldValue(e.target.name, file);
  };

  const handleMultipleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    formik.setFieldValue(e.target.name, files);
  };

  const multipleFiles = [
    'English Proof',
    'Technical test',
    'Reference letter',
    'Additional documents',
  ];

  let stepView;
  if (isLoading) {
    return <Loader />;
  }

  const variableText: { [key: string]: string } = {
    'Personal statement': `This is one of the most important sections of your application, please make sure to \
      write at least 1 page where you explain why you want to study ${postulation?.data.program.name} in \
      ${postulation?.data.program.school_name} . It also gives you the opportunity to showcase why you are \
      a good fit for the program.`,
    'Additional documents': postulation?.data.additional_documents_text || '',
  };

  const variableTextElements = ['Personal statement', 'Additional documents'];

  if (currentStep === 1) {
    stepView = (
      <div className={isMobile ? styles.mobileBody : styles.desktopBody}>
        <div className={styles.filters}>
          <div className={styles.filter}>
            <Text size={isMobile ? 14 : 16} weight={700}>
              Intake Year
            </Text>
            <select onChange={(event) => handleFilterChange(event, 'intake_date')}>
              <option value="all">All</option>
              {intakeDatesOptions}
            </select>
          </div>
          <div className={styles.filter}>
            <Text size={isMobile ? 14 : 16} weight={700}>
              Attendance
            </Text>
            <select onChange={(event) => handleFilterChange(event, 'attendance')}>
              <option value="all">All</option>
              {attendaceFilterOptions}
            </select>
          </div>
          <div className={styles.filter}>
            <Text size={isMobile ? 14 : 16} weight={700}>
              Location
            </Text>
            <select onChange={(event) => handleFilterChange(event, 'location')}>
              <option value="all">All</option>
              {locationFilterOptions}
            </select>
          </div>
        </div>
        <div className={styles.item}>
          <Text size={isMobile ? 14 : 16} weight={700}>
            Study mode
          </Text>
        </div>
        {!isMobile && (
          <div className={`${styles.headerStudyOption} row`}>
            <div className="col-1">
              <Text inlineStyle={{ visibility: 'hidden' }}>Icon</Text>
            </div>
            <div className="col-2">
              <Text size={16} weight={500} color={'secondary'}>
                Study Mode
              </Text>
            </div>
            <div className="col-2">
              <Text size={16} weight={500} color={'secondary'}>
                Intake Month
              </Text>
            </div>
            <div className="col-2">
              <Text size={16} weight={500} color={'secondary'}>
                Course Length
              </Text>
            </div>
            <div className="col-3">
              <Text size={16} weight={500} color={'secondary'}>
                Tuition Fee
              </Text>
            </div>
          </div>
        )}
        <div className={styles.studyOptionsContainer}>
          {filterData?.map((studyOption: StudyOption) =>
            studyOption.intake_dates?.map((intakeDate: IntakeDate) => (
              <StudyOptionCard
                studyOption={studyOption}
                intakeDate={intakeDate}
                key={intakeDate.id}
                handleClick={() => handleClick({ studyOption, intakeDate })}
                active={currentIntakeDate?.id === intakeDate.id}
              />
            ))
          )}
        </div>
        <div className={styles.buttonContainer}>
          <button className={`${styles.button} btn`} onClick={() => handleSubmitChoice('next')}>
            <Text size={16} weight={700}>
              Next
            </Text>
          </button>
        </div>
      </div>
    );
  } else if (currentStep === 2) {
    stepView = (
      <div className={styles.container}>
        {isMobile ? (
          <>
            {!readyForSubmit ? (
              <div className={styles.documentStatusCard}>
                <div className={styles.msgContainer}>
                  <img src={Star} alt="star" className={`${styles.img}`} />
                  <Text size={14} weight={600}>
                    {postulation?.data.status === 'preparing'
                      ? 'Upload & fill all required documents to submit an application'
                      : postulation?.data.status === 'need_revision'
                      ? 'We found errors in your application please submit again the following documents'
                      : 'Expert is viewing your documents'}
                    {/* {postulation?.data.status === 'submitted'
                      ? 'Expert is viewing your documents'
                      : 'We found errors in your application please submit again the following documents'} */}
                  </Text>
                  <img src={Star} alt="star" className={`${styles.img}`} />
                </div>
                {postulation?.data.status === 'preparing' && (
                  <button disabled className={`${styles.exitButton} btn`}>
                    <Text size={16} weight={700}>
                      Submit
                    </Text>
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.documentStatusCardReady}>
                {!postulationSubmitted ? (
                  <>
                    <div className={styles.postulationReadyText}>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                      <Text size={20} weight={600} color="#19234F">
                        All your documents are ready!
                      </Text>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                    </div>
                    <div className={styles.postulationReadyText}>
                      <Text size={20} weight={600} color="#19234F">
                        Submit now
                      </Text>
                    </div>
                    <button
                      className={`${styles.exitButton} btn`}
                      type="submit"
                      form={handleFormName(currentStep)}
                      onClick={() => setSubmitFormButton(true)}
                      disabled={isLoadingFiles || isLoadingPostulation}
                    >
                      {isLoadingFiles ? (
                        <div className={`spinner-border ${styles.loading}`} role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <Text size={16} weight={700}>
                          Submit
                        </Text>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.postulationReadyText}>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                      <Text
                        size={20}
                        weight={600}
                        color="#19234F"
                        inlineStyle={{ textAlign: 'center' }}
                      >
                        Our experts are reviewing your application
                      </Text>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                    </div>
                    <button className={`${styles.exitButton} btn`}>
                      <Text size={16} weight={700}>
                        Submitted
                      </Text>
                    </button>
                  </>
                )}
              </div>
            )}
            <div className={styles.textContainer}>
              <Text size={14} weight={600}>
                Checklist
              </Text>
            </div>
          </>
        ) : (
          <>
            {!readyForSubmit && postulation?.data.status !== 'need_revision' ? (
              <div className={styles.documentStatusCard}>
                <div className={styles.robotWrap}>
                  <img src={Robot} className={styles.robot}></img>
                  <div className={styles.robotText}>
                    <Text size={16} weight={500} color="primary">
                      Our expert will review all your documents!
                    </Text>
                  </div>
                </div>
                <div className={styles.row}>
                  {/* <div className={styles.wrap} style={{ width: '150px', marginRight: '57px' }}>
                  <i className={`bi bi-check-circle ${styles.active}`}></i>
                  <Text size={16} weight={500} color="primary">
                    Application Fee
                  </Text>
                </div> */}
                  <div className={styles.wrap}>
                    <i
                      className={
                        profileCompleted
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Profile
                    </Text>
                  </div>
                  <div className={styles.wrap}>
                    <i
                      className={
                        postulation?.data.transcripts != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.transcripts
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Transcript
                    </Text>
                  </div>
                  <div className={styles.wrap}>
                    <i
                      className={
                        postulation?.data.cv != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.cv
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      CV
                    </Text>
                  </div>
                  <div className={styles.wrap}>
                    <i
                      className={
                        postulation?.data.english_score != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.englishProof
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Proof of English Score
                    </Text>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.wrap}>
                    <i
                      className={
                        postulation?.data.personal_statement != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.personalStatement
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Personal Statement
                    </Text>
                  </div>
                  <div className={styles.wrap}>
                    <i
                      className={
                        postulation?.data.degree_certificate != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.degreeCertificate
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Degree certificate
                    </Text>
                  </div>
                  <div
                    className={styles.wrap}
                    style={
                      postulation?.data.program.program_exams.filter(
                        (item: ProgramExam) => item.exam.name == 'GMAT'
                      ).length
                        ? { display: '' }
                        : { display: 'none' }
                    }
                  >
                    <i
                      className={
                        postulation?.data.technical_score != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.technicalTest
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Proof of GMAT/GRE Score
                    </Text>
                  </div>
                  <div className={styles.wrap} style={{ display: 'none' }}>
                    <i
                      className={
                        postulation?.data.recommendation_letter != 'missing'
                          ? `bi bi-check-circle ${styles.active}`
                          : formik.values.referenceLetter
                          ? `bi bi-check-circle ${styles.active}`
                          : 'bi bi-check-circle'
                      }
                    ></i>
                    <Text size={16} weight={500} color="primary">
                      Recommendation letter
                    </Text>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.documentStatusCardReady}>
                {!postulationSubmitted ? (
                  <>
                    <div className={styles.postulationReadyText}>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                      <Text size={20} weight={600} color="#19234F">
                        All your documents are ready! Submit now
                      </Text>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                    </div>
                    <button
                      className={`${styles.exitButton} btn`}
                      type="submit"
                      form={handleFormName(currentStep)}
                      onClick={() => setSubmitFormButton(true)}
                      disabled={isLoadingFiles || isLoadingPostulation}
                    >
                      {isLoadingFiles && submitFormButton ? (
                        <div className={`spinner-border ${styles.loading}`} role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <Text size={16} weight={700}>
                          Submit Now
                        </Text>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.postulationReadyText}>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                      <Text size={20} weight={600} color="#19234F">
                        {postulation?.data.status === 'need_revision'
                          ? 'We found errors in your application please submit again the following documents'
                          : 'Expert is viewing your documents'}
                      </Text>
                      <img src={Star} alt="star" className={`${styles.img}`} />
                    </div>
                    {postulation?.data.status !== 'need_revision' && (
                      <button className={`${styles.exitButton} btn`}>
                        <Text size={16} weight={700}>
                          Submitted
                        </Text>
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
        <form className={styles.cardFlex} onSubmit={formik.handleSubmit} id="filesForm">
          <UploadCard
            name="Profile"
            status={profileCompleted ? 'approved' : 'missing'}
            formName="none"
            requiredExams={[]}
            handleFile={handleFile}
            text={documentText['profile']}
            mandatory={true}
            readOnly={false}
            idPostulation={postulation?.data.id}
          />
          {postulation?.data.requirements?.map((requirement: PostulationRequirement) => (
            <UploadCard
              key={requirement.id}
              name={requirement.name}
              status={handleDocumentStatus(
                requirement.formName as UploadFilesKey,
                requirement.status
              )}
              requiredExams={requirement.required_exams}
              formName={requirement.formName}
              handleFile={
                multipleFiles.includes(requirement.name) ? handleMultipleFiles : handleFile
              }
              text={
                variableTextElements.includes(requirement.name)
                  ? variableText[requirement.name]
                  : documentText[requirement.name]
              }
              mandatory={requirement.mandatory}
              readOnly={false}
              idPostulation={postulation?.data.id}
              files={requirement.documents}
              formik={formik}
            />
          ))}
        </form>
        {isMobile && (
          <div className={styles.mobileButtons}>
            <button
              className={`${styles.button} btn`}
              type="submit"
              form={handleFormName(currentStep)}
              onClick={() => setSubmitFormButton(false)}
              disabled={isLoadingFiles || isLoadingPostulation}
            >
              {isLoadingFiles ? (
                <div className={`spinner-border ${styles.loading}`} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <Text size={16} weight={700}>
                  Save & Leave
                </Text>
              )}
            </button>
          </div>
        )}
      </div>
    );
  } else if (currentStep === 3) {
    stepView = (
      <div className={styles.container}>
        <div className={styles.documentStatusCardThree}>
          <div className={styles.msg}>
            <img src={Star} alt="star" className={`${styles.img}`} />
            <Text size={isMobile ? 14 : 20} weight={600}>
              {isMobile
                ? 'Application successfully submitted!'
                : 'Your Application successfully submitted!'}
            </Text>
            <img src={Star} alt="star" className={`${styles.img}`} />
          </div>
          <Text
            size={isMobile ? 14 : 20}
            weight={600}
            inlineStyle={isMobile ? { color: '#19234F' } : { color: '#79AAF7' }}
          >
            Track your status here
          </Text>
        </div>
        <div className={styles.subText}>
          <Text size={isMobile ? 16 : 20} weight={600}>
            {isMobile ? 'Status' : 'Track Status'}
          </Text>
        </div>
        <div className={styles.cardsContainer}>
          <CollapseCard
            name="Submittied to school"
            status="active"
            text="We submitted your application to the school, so everything is ready for you to wait for a final answer"
          />
          {postulation?.data.status === 'additional_files' && (
            <CollapseCard
              name="Submit additional documents"
              status="ready"
              text="Our expert will get in touch with you, the school asked for more information and additional documents to process your application."
            />
          )}
          <CollapseCard
            name="Waiting for school response"
            status={postulation?.data.status === 'additional_files' ? 'active' : 'pending'}
            text="Whenever the school gives us a response we will get back to you. Is possible also that the school will contact you directly through the email address you used to register at Unify.. This can take as little as 2 weeks up to a couple of months depending on the school."
          />
        </div>
        {isMobile && (
          <button
            onClick={() => {
              navigate('/home');
              navigateTop();
            }}
            className={`${styles.exitButton} btn`}
          >
            <Text size={16} weight={700}>
              Save & Leave
            </Text>
          </button>
        )}
      </div>
    );
  } else if (currentStep === 4) {
    stepView = (
      <div className={styles.container}>
        <div className={styles.documentStatusCardFourth}>
          <div className={styles.msg}>
            <img src={Star} alt="star" className={`${styles.img}`} />
            <Text size={isMobile ? 14 : 20} weight={600}>
              Congratulation! You got an offer
            </Text>
            <img src={Star} alt="star" className={`${styles.img}`} />
          </div>
          <Text
            size={isMobile ? 12 : 16}
            weight={400}
            inlineStyle={{ marginBottom: `${isMobile ? '4px' : '4px'}` }}
          >
            Pay the deposit fee to accept your offer
          </Text>
          <button
            className={styles.acceptButton}
            disabled={postulation?.data.university_fee !== 'approved'}
            onClick={handleConfirmOffer}
          >
            <Text size={isMobile ? 14 : 16} weight={700}>
              {isMobile ? 'Accept' : 'Accept offer'}
            </Text>
          </button>
        </div>
        <div className={styles.subText}>
          <Text size={isMobile ? 14 : 20} weight={600}>
            Secure Spot
          </Text>
        </div>
        <form
          className={styles.cardsContainer}
          onSubmit={formik.handleSubmit}
          id="universityFeeForm"
        >
          <CollapseCard
            name="Offer letter received"
            status="active"
            text="Congratulations for getting accepted! We are delighted that you have been offered a spot in the school of your dreams."
          />
          <CollapseCard
            name="Pay deposit fee"
            status={
              postulation && postulation?.data.university_fee != 'missing'
                ? postulation.data.university_fee
                : formik.values.universityFee
                ? 'uploaded'
                : 'missing'
            }
            text="To secure your place at the program, the university requires you to pay the deposit fee in advance. Normally deposits need to be paid up to 2 weeks after receiving an offer.  Please upload a copy of the transaction so we can help you informing the university that it was paid."
            button="upload"
            handleFile={handleFile}
          />
          <CollapseCard
            name="University acceptance letter"
            status="ready"
            text="You are almost ready to go, please review the enrollment section of your dashboard to learn the next steps for you to start the new stage of your exciting journey."
          />
        </form>
        {isMobile && (
          <button
            onClick={() => {
              navigate('/home');
              navigateTop();
            }}
            className={`${styles.exitButton} btn`}
          >
            <Text size={16} weight={700}>
              Save & Leave
            </Text>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={isMobile ? '' : styles.desktopContainer}>
      <div
        className={isMobile ? '' : styles.leftColumn}
        style={{ width: isBackoffice ? '100%' : '72%' }}
      >
        <div className={isMobile ? styles.mobileHeader : styles.desktopHeader}>
          {!isMobile && (
            <div className={styles.ubicationText}>
              <Link to=".." relative="path">
                <Text size={16} weight={500}>
                  {'Dashboard >'}
                </Text>
              </Link>
              <Link to=".." relative="path">
                <Text size={16} weight={500}>
                  {'Application List >'}
                </Text>
              </Link>
              <Link
                to={detailUrl(
                  'schools',
                  postulation?.data.program.school_id,
                  postulation?.data.program.school_name
                )}
                relative="path"
              >
                <Text size={16} weight={500}>
                  {postulation?.data.program.school_name}
                </Text>
              </Link>
            </div>
          )}
          {isMobile && <i onClick={() => navigate('/home')} className="bi bi-chevron-left"></i>}
          {!isMobile ? (
            <div className={styles.subcontainer}>
              <Text size={20} weight={700}>
                {postulation?.data.program.name}
              </Text>
              <button
                className={`${styles.exitButton} btn`}
                type="submit"
                form={handleFormName(currentStep)}
                onClick={
                  currentStep === 1
                    ? () => handleSubmitChoice('save')
                    : () => setSubmitFormButton(false)
                }
                disabled={isLoadingFiles || isLoadingPostulation}
              >
                {isLoadingFiles && !submitFormButton ? (
                  <div className={`spinner-border ${styles.loading}`} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <Text size={16} weight={700}>
                    Save & Leave
                  </Text>
                )}
              </button>
            </div>
          ) : (
            <Text size={20} weight={700}>
              {postulation?.data.program.name}
            </Text>
          )}
          <Text size={16} weight={500}>
            {postulation?.data.program.school_name}
          </Text>
          <div className={styles.stepperContainer}>
            <div className={styles.upperStepper}>
              <div className={styles.stepContainer}>
                <div className={`${styles.stepperCircle} ${styles.active}`}>1</div>
                <div
                  className={
                    currentStep == 1 ? `${styles.active} ${styles.stepName}` : styles.stepName
                  }
                >
                  Confirm information
                </div>
              </div>
              <hr className={currentStep < 2 ? styles.bar : `${styles.bar} ${styles.active}`}></hr>
              <div className={styles.stepContainer}>
                <div
                  className={
                    currentStep >= 2
                      ? `${styles.stepperCircle} ${styles.active}`
                      : styles.stepperCircle
                  }
                >
                  2
                </div>
                <div
                  className={
                    currentStep == 2 ? `${styles.active} ${styles.stepName}` : styles.stepName
                  }
                >
                  Document Checklist
                </div>
              </div>
              <hr className={currentStep < 3 ? styles.bar : `${styles.bar} ${styles.active}`}></hr>
              <div className={styles.stepContainer}>
                <div
                  className={
                    currentStep >= 3
                      ? `${styles.stepperCircle} ${styles.active}`
                      : styles.stepperCircle
                  }
                >
                  3
                </div>
                <div
                  className={
                    currentStep == 3 ? `${styles.active} ${styles.stepName}` : styles.stepName
                  }
                >
                  Application Submitted
                </div>
              </div>
              <hr className={currentStep < 4 ? styles.bar : `${styles.bar} ${styles.active}`}></hr>
              <div className={styles.stepContainer}>
                <div
                  className={
                    currentStep == 4
                      ? `${styles.stepperCircle} ${styles.active}`
                      : styles.stepperCircle
                  }
                >
                  4
                </div>
                <div
                  className={
                    currentStep == 4 ? `${styles.active} ${styles.stepName}` : styles.stepName
                  }
                >
                  Final Result
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.bodyContainer}>{stepView}</div>
      </div>
      {!isMobile && !isBackoffice && (
        <div className={styles.rightColumn}>
          <div className={styles.item}>
            <Text size={20} weight={600}>
              My expert
            </Text>
            <ExpertCard />
          </div>
          <div className={styles.item}>
            <div className={styles.servicesBox}>
              <img src={ServicesPhoto} className={styles.image}></img>
              <Text size={16} weight={500} inlineStyle={{ width: '80%', textAlign: 'center' }}>
                Our trusted partners help you maximize your chances
              </Text>
              <div className={styles.button}>Discover services</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default PostulationSetUp;
